import { zodResolver } from "@hookform/resolvers/zod";
import { Warning } from "@mui/icons-material";
import { Alert, Box, Checkbox, FormControl, FormHelperText, FormLabel, Stack } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { ApiCreateQuestPayload, ApiQuest, ClimbType } from "griptonite-api-client/types";
import { useEffect, useMemo } from "react";
import { FormProvider, useController, useForm, useFormContext } from "react-hook-form";

import { useCreateQuestMutation, useQuestQuery, useUpdateQuestMutation } from "@/hooks/quests";
import { useCurrentVenueUuid, useVenueQuery } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import CheckboxInput from "../forms/CheckboxInput";
import { MediaSelectInput } from "../forms/MediaInput";
import MonthInput from "../forms/MonthInput";
import TextInput, { DateInput, TextareaInput } from "../forms/TextInput";
import BottomButtons, { QUEST_PATH, questStepLink } from "./BottomButtons";
import {
  currentMonth,
  makeQuestDetailsFormSchema,
  QuestDetailsFormValues,
  questDetailsFormValuesToCreatePayload,
  questDetailsFormValuesToPayload,
  questToDetailsFormValues,
} from "./forms";
import PublishWarningsAlert from "./PublishWarningsAlert";
import QuestProfileTabs from "./QuestProfileTabs";
import QuestTabLayout from "./QuestTabLayout";

const CLIMB_TYPE_LABELS: Record<string, string> = {
  BOULDER: "Bouldering",
  SPORT: "Sport",
};

const DisciplineFields = ({ options, locked }: { options: ClimbType[]; locked: boolean }) => {
  const { control } = useFormContext<QuestDetailsFormValues>();
  const { field, fieldState } = useController({ name: "climbTypes", control });

  return (
    <FormControl error={Boolean(fieldState.error)}>
      <FormLabel>Disciplines</FormLabel>
      <Stack direction="row" spacing={2}>
        {options.map((climbType) => (
          <Checkbox
            key={climbType}
            label={CLIMB_TYPE_LABELS[climbType] ?? climbType}
            disabled={locked}
            checked={field.value.includes(climbType)}
            onChange={(event) =>
              field.onChange(
                event.target.checked
                  ? [...field.value, climbType]
                  : field.value.filter((value) => value !== climbType),
              )
            }
          />
        ))}
      </Stack>
      {locked ? (
        <FormHelperText>
          Disciplines can&apos;t be changed once the quest has started.
        </FormHelperText>
      ) : null}
      {fieldState.error?.message ? (
        <FormHelperText>{fieldState.error.message}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

const FormFields = ({
  periodLocked,
  venueClimbTypes,
  timezone,
}: {
  periodLocked: boolean;
  venueClimbTypes: ClimbType[];
  timezone: string;
}) => {
  const { watch } = useFormContext<QuestDetailsFormValues>();
  const startTime = watch("startTime");
  const isEndless = watch("isEndless");
  const type = watch("type");
  const month = watch("month");
  // The current month has by definition already started; a future one hasn't. A started quest's
  // month can't be changed, so there's nothing left to warn about.
  const showMonthStartedWarning = !periodLocked && !!month && month === currentMonth(timezone);

  return (
    <Stack spacing={2}>
      <TextInput name="name" label="Quest name" />
      <TextareaInput name="description" label="Description" />
      <MediaSelectInput name="coverImage" label="Cover image" accept="image/*" multiple={false} />
      {/* A venue with one discipline leaves nothing to choose; the form still saves that one. */}
      {type === "LEADERBOARD_POINTS" && venueClimbTypes.length > 1 ? (
        <DisciplineFields options={venueClimbTypes} locked={periodLocked} />
      ) : null}
      {type === "LEADERBOARD_POINTS" ? (
        <>
          <MonthInput
            name="month"
            label="Month"
            disabled={periodLocked}
            minMonth={currentMonth(timezone)}
          />
          {showMonthStartedWarning ? (
            <Alert startDecorator={<Warning />} color="warning">
              This month has already started. To run the quest for a full month, choose a later
              month.
            </Alert>
          ) : null}
        </>
      ) : (
        <>
          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <DateInput
                name="startTime"
                label="Starts at"
                placeholder=""
                disabled={periodLocked}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              {!isEndless ? (
                <DateInput
                  name="endTime"
                  label="Ends at"
                  minDate={startTime ? format(startTime, "yyyy-MM-dd") : undefined}
                  defaultTime="23:59"
                  placeholder=""
                />
              ) : null}
            </Box>
          </Stack>
          <Box>
            {/* in a box as there's a weird sibling style rule on helper text for checkboxes */}
            <CheckboxInput name="isEndless" label="Never ends" />
          </Box>
        </>
      )}
    </Stack>
  );
};

// The form can't be built until the venue's timezone is known: month boundaries, and the month
// shown for an existing quest, are both defined in it. Guessing (say, UTC) would silently save the
// wrong period. The venue's climb types are also the disciplines on offer. Without a `questUuid`,
// the form creates a quest of `questType`.
export default function QuestDetailsForm({
  questUuid,
  questType,
}: {
  questUuid?: string;
  questType?: ApiCreateQuestPayload["type"];
}) {
  const routeVenueUuid = useCurrentVenueUuid();
  const { data: quest } = useQuestQuery(questUuid, routeVenueUuid);
  // The quest's own venue, not necessarily the one in the URL. A new quest goes in the URL's.
  const { data: venue } = useVenueQuery(quest ? quest.venue.uuid : routeVenueUuid);

  if ((questUuid && !quest) || !venue) {
    return null;
  }
  return (
    <QuestDetailsFormInner
      quest={quest}
      questType={questType}
      venueUuid={venue.uuid}
      timezone={venue.timezone}
      venueClimbTypes={venue.climbTypes}
      routeVenueUuid={routeVenueUuid}
    />
  );
}

const QuestDetailsFormInner = ({
  quest,
  questType,
  venueUuid,
  timezone,
  venueClimbTypes,
  routeVenueUuid,
}: {
  quest?: ApiQuest;
  questType?: ApiCreateQuestPayload["type"];
  venueUuid: string;
  timezone: string;
  venueClimbTypes: ClimbType[];
  routeVenueUuid?: string;
}) => {
  const createMutation = useCreateQuestMutation();
  const updateMutation = useUpdateQuestMutation(quest?.uuid, routeVenueUuid);
  const navigate = useNavigate();

  const defaultValues = useMemo(
    () => questToDetailsFormValues(quest, timezone, venueClimbTypes, questType),
    [quest, timezone, venueClimbTypes, questType],
  );
  // A new quest, or disciplines the quest has never saved, still need saving even if nothing was
  // touched.
  const needsSave = !quest || (quest.type === "LEADERBOARD_POINTS" && !quest.climbTypes?.length);
  const schema = useMemo(
    () => makeQuestDetailsFormSchema(timezone, defaultValues.month),
    [timezone, defaultValues.month],
  );

  const methods = useForm<QuestDetailsFormValues>({
    mode: "onBlur",
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const onSubmit = async (values: QuestDetailsFormValues) => {
    if (!quest) {
      const created = await createMutation.mutateAsync(
        questDetailsFormValuesToCreatePayload(values, timezone, venueUuid),
        {
          onError: (error) => handleFormError(error, methods),
        },
      );
      navigate({
        to: `${QUEST_PATH}/profile/rules`,
        params: { venueUuid, questUuid: created.uuid },
        replace: true,
      });
      return;
    }
    const saved = await updateMutation.mutateAsync(
      questDetailsFormValuesToPayload(values, defaultValues, timezone, quest.climbTypes),
      {
        onError: (error) => handleFormError(error, methods),
      },
    );
    if (saved.isHidden) {
      navigate(questStepLink("rules"));
    }
  };

  return (
    <QuestTabLayout>
      <QuestProfileTabs questUuid={quest?.uuid} />
      <Stack
        sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}
        component="form"
        autoComplete="off"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <FormProvider {...methods}>
          <Stack sx={{ padding: 1.5, gap: 1, flex: 1, overflowY: "auto", minHeight: 0 }}>
            <PublishWarningsAlert tab="details" quest={quest} />
            <FormErrorAlert />
            <FormFields
              periodLocked={!!quest && quest.status !== "UPCOMING"}
              venueClimbTypes={venueClimbTypes}
              timezone={timezone}
            />
          </Stack>
          <Box sx={{ padding: 1.5, flexShrink: 0 }}>
            <BottomButtons
              quest={quest}
              step="details"
              formState={{
                isSubmitting: methods.formState.isSubmitting,
                isDirty: methods.formState.isDirty || needsSave,
              }}
            />
          </Box>
        </FormProvider>
      </Stack>
    </QuestTabLayout>
  );
};
